import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from "react";

/**
 * Garść własnych komponentów zamiast biblioteki.
 *
 * Cała aplikacja to sześć ekranów, a to, czego naprawdę będzie trzeba przy
 * kolejnych grach — plansza Catana, stojak Scrabble — i tak nie istnieje
 * w żadnej bibliotece. Za to każda z nich dokłada setki kilobajtów do PWA
 * ładowanej przez LTE w tunelu.
 *
 * Wspólny mianownik wszystkich tych komponentów: **duże cele dotykowe**.
 * Telefon trzymany w trzęsącym się aucie wybacza znacznie mniej niż mysz.
 */

type Wariant = "glowny" | "zwykly" | "cichy" | "gorny" | "blad";

const WARIANTY: Record<Wariant, string> = {
  glowny: "bg-akcent text-tlo font-semibold active:bg-akcent-ciemny",
  zwykly: "bg-panel-jasny text-tekst border border-obrys active:bg-panel",
  cichy: "bg-transparent text-tekst-slaby active:text-tekst",
  gorny: "bg-uwaga text-tlo font-semibold",
  blad: "bg-blad text-tekst font-semibold",
};

interface PrzyciskProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  wariant?: Wariant;
  pelna?: boolean;
}

export function Przycisk({
  wariant = "zwykly",
  pelna = false,
  className = "",
  ...props
}: PrzyciskProps) {
  return (
    <button
      {...props}
      className={[
        // min-h-14 to około 56 px — powyżej progu, który da się trafić kciukiem
        // bez patrzenia i bez celowania.
        "min-h-14 px-5 rounded-2xl text-lg transition-colors",
        "disabled:opacity-40 disabled:pointer-events-none",
        pelna ? "w-full" : "",
        WARIANTY[wariant],
        className,
      ].join(" ")}
    />
  );
}

export function Pole({ className = "", ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={[
        "w-full min-h-14 px-4 rounded-2xl",
        "bg-panel border border-obrys text-tekst placeholder:text-tekst-slaby",
        "focus:outline-none focus:border-akcent",
        className,
      ].join(" ")}
    />
  );
}

export function Karta({
  children,
  className = "",
  onClick,
}: {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  const Element = onClick ? "button" : "div";
  return (
    <Element
      onClick={onClick}
      className={[
        "block w-full text-left bg-panel border border-obrys rounded-2xl p-4",
        onClick ? "active:bg-panel-jasny transition-colors" : "",
        className,
      ].join(" ")}
    >
      {children}
    </Element>
  );
}

/**
 * Rama ekranu: pasek tytułowy przyklejony u góry, treść przewijana,
 * marginesy bezpieczne dla telefonów z wcięciem.
 */
export function Ekran({
  tytul,
  podtytul,
  akcja,
  children,
  stopka,
}: {
  tytul: string;
  podtytul?: ReactNode;
  akcja?: ReactNode;
  children: ReactNode;
  stopka?: ReactNode;
}) {
  return (
    <div className="flex flex-col h-full">
      <header className="bezpieczna-gora px-4 pb-3 border-b border-obrys flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-semibold truncate">{tytul}</h1>
          {podtytul ? <div className="text-sm text-tekst-slaby truncate">{podtytul}</div> : null}
        </div>
        {akcja}
      </header>

      <main className="flex-1 overflow-y-auto px-4 py-4 space-y-3">{children}</main>

      {stopka ? (
        <footer className="bezpieczny-dol px-4 pt-3 border-t border-obrys space-y-2">
          {stopka}
        </footer>
      ) : null}
    </div>
  );
}

export function Komunikat({ children, rodzaj = "info" }: { children: ReactNode; rodzaj?: "info" | "blad" }) {
  return (
    <div
      className={[
        "rounded-2xl px-4 py-3 text-sm",
        rodzaj === "blad" ? "bg-blad/15 text-blad border border-blad/40" : "bg-panel text-tekst-slaby",
      ].join(" ")}
    >
      {children}
    </div>
  );
}

/** Wskaźnik połączenia. W aucie to informacja pierwszej potrzeby. */
export function Polaczenie({ status }: { status: "offline" | "connecting" | "online" }) {
  if (status === "online") return null;
  return (
    <div
      className={[
        "text-sm px-3 py-2 rounded-xl text-center",
        status === "offline" ? "bg-blad/20 text-blad" : "bg-uwaga/20 text-uwaga",
      ].join(" ")}
    >
      {status === "offline" ? "Brak połączenia — odpowiedzi zapiszą się lokalnie" : "Łączę…"}
    </div>
  );
}

/**
 * `zielona` to wariant przygaszony, nie ten sam co `aktywna`.
 *
 * Odznaka „w słowniku" pojawia się przy większości odpowiedzi w rundzie —
 * gdyby była pełnym, jaskrawym akcentem, ekran sprzeciwów zamieniłby się
 * w ścianę zieleni i przestałaby cokolwiek wyróżniać. Ma nieść jedną
 * informację: „tego nie musisz sprawdzać".
 */
export function Odznaka({
  children,
  aktywna = false,
  zielona = false,
}: {
  children: ReactNode;
  aktywna?: boolean;
  zielona?: boolean;
}) {
  const styl = aktywna
    ? "bg-akcent text-tlo"
    : zielona
      ? "bg-akcent/20 text-akcent"
      : "bg-panel-jasny text-tekst-slaby";

  return <span className={`inline-block px-2.5 py-1 rounded-lg text-xs ${styl}`}>{children}</span>;
}
